import React, { useState } from 'react';
import './FileRelationshipModal.css';

interface FileRelationship {
  source_file: string;
  source_column: string;
  target_file: string;
  target_columns: string[];
  relationship_type: string;
  confidence: number;  // Changed from string to number
  transform?: string;
  lag_days?: number;
  matching_strategy?: string;
  reasoning?: string;
}

interface FileRelationshipModalProps {
  relationships: FileRelationship[];
  filesAnalyzed: string[];  // List of filenames
  recommendations?: string[];
  confidenceScore?: number;
  contextId: string;
  sessionId: string;
  onComplete: (relationships: any) => void;
  onSkip: () => void;
}

const FileRelationshipModal: React.FC<FileRelationshipModalProps> = ({
  relationships,
  filesAnalyzed,
  recommendations,
  confidenceScore,
  contextId,
  sessionId,
  onComplete,
  onSkip
}) => {
  const [editedRelationships, setEditedRelationships] = useState(relationships);
  const [showDetails, setShowDetails] = useState<Record<number, boolean>>({});

  const handleConfirm = () => {
    onComplete({
      relationships: editedRelationships,
      filesAnalyzed,
      recommendations,
      confidenceScore
    });
  };

  const toggleDetails = (index: number) => {
    setShowDetails(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getConfidenceEmoji = (confidence: number) => {
    if (confidence >= 0.8) return '🟢';  // High confidence
    if (confidence >= 0.5) return '🟡';  // Medium confidence
    if (confidence >= 0.3) return '🔴';  // Low confidence
    return '⚪';  // Very low/unknown
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.8) return 'high';
    if (confidence >= 0.5) return 'medium';
    if (confidence >= 0.3) return 'low';
    return 'very low';
  };

  const getRelationshipIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'primary_key': return '🔑';
      case 'foreign_key': return '🔗';
      case 'lookup': return '🔍';
      case 'join': return '🔀';
      case 'consolidation': return '📊';
      case 'reconciliation': return '🤝';
      default: return '📌';
    }
  };

  // Format the files for display
  const formatFilesList = (files: string | string[]) => {
    if (Array.isArray(files)) {
      return files.join(', ');
    }
    // If it's a string, try to split it if it looks like concatenated filenames
    if (typeof files === 'string' && files.includes('.xlsx')) {
      // Split on file extensions and rejoin properly
      const parts = files.split(/(?=[\w\s\(\)]+\.\w+)/);
      return parts.filter(p => p.trim()).join(', ');
    }
    return files;
  };

  return (
    <div className="file-relationship-modal">
      <div className="modal-header">
        <h2>File Relationship Review</h2>
        <p className="modal-subtitle">
          Analyzing relationships between {formatFilesList(filesAnalyzed)}
        </p>
      </div>

      <div className="modal-body">
        {relationships.length === 0 ? (
          <div className="no-relationships">
            <p>No explicit relationships detected between files.</p>
            <p className="hint">The AI will analyze the files independently.</p>
          </div>
        ) : (
          <>
            <div className="relationships-list">
              {relationships.map((rel, index) => (
                <div key={index} className="relationship-card">
                  <div className="relationship-header" onClick={() => toggleDetails(index)}>
                    <div className="relationship-summary">
                      <span className="relationship-icon">{getRelationshipIcon(rel.relationship_type)}</span>
                      <span className="source-file">{rel.source_file}</span>
                      <span className="arrow">→</span>
                      <span className="target-file">{rel.target_file}</span>
                    </div>
                    <div className="relationship-meta">
                      <span className="relationship-type">{rel.relationship_type}</span>
                      <span className="confidence-badge">
                        {getConfidenceEmoji(rel.confidence)} {getConfidenceLabel(rel.confidence)}
                      </span>
                      <button className="toggle-details">
                        {showDetails[index] ? '▼' : '▶'}
                      </button>
                    </div>
                  </div>

                  {showDetails[index] && (
                    <div className="relationship-details">
                      <div className="detail-row">
                        <label>Source Column:</label>
                        <span>{rel.source_column}</span>
                      </div>
                      <div className="detail-row">
                        <label>Target Columns:</label>
                        <span>{rel.target_columns.join(', ')}</span>
                      </div>
                      {rel.matching_strategy && (
                        <div className="detail-row">
                          <label>Matching Strategy:</label>
                          <span>{rel.matching_strategy}</span>
                        </div>
                      )}
                      {rel.transform && (
                        <div className="detail-row">
                          <label>Transform:</label>
                          <span>{rel.transform}</span>
                        </div>
                      )}
                      {rel.lag_days !== undefined && (
                        <div className="detail-row">
                          <label>Lag Days:</label>
                          <span>{rel.lag_days}</span>
                        </div>
                      )}
                      {rel.reasoning && (
                        <div className="detail-row full-width">
                          <label>Reasoning:</label>
                          <p className="reasoning-text">{rel.reasoning}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {recommendations && recommendations.length > 0 && (
              <div className="recommendations-section">
                <h3>Recommendations</h3>
                <ul>
                  {recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {confidenceScore !== undefined && (
              <div className="confidence-section">
                <label>Overall Confidence:</label>
                <div className="confidence-bar">
                  <div 
                    className="confidence-fill"
                    style={{ width: `${confidenceScore * 100}%` }}
                  />
                </div>
                <span className="confidence-value">{Math.round(confidenceScore * 100)}%</span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onSkip}>
          Skip Review
        </button>
        <button className="btn btn-primary" onClick={handleConfirm}>
          Confirm Relationships
        </button>
      </div>
    </div>
  );
};

export default FileRelationshipModal;